/* eslint-disable react/display-name */
/**
 * @file Transfer inputs for robots
 * @author Max van Loosbroek
 */

import { ArrowRightAlt } from '@material-ui/icons'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TbDashDetailedItemRow, TbDashDetailedItem } from 'tinybots-react-components'
import { RootState } from '../../../common/redux/root.reducer'
import { AppDispatch } from '../../../common/redux/store'
import { useDebouncedFunction } from '../../overview/Detailed/LinkDialog/Robot/hooks'
import { getRobotRef } from '../../overview/stringTransformers'
import { SummaryRobot, DetailedRobot, SummarySubscription } from '../../overview/types/types'
import {
  dashtransferSelector,
  robotsSearchSelector,
  robotsTaasSearchSelector,
  setRobotSerial
} from '../redux/reducer'
import { getNewRobot, searchRobotsDashtransfer } from '../redux/thunks'
import { DashtransferState } from '../types'
import { getHardwareTypeIconPath } from '../../overview/getHardwareTypeIconPath'
import { getHardwareTypeName } from '../../overview/getHardwareTypeName'
import styled from 'styled-components'

const Label = styled.div`
  font-size: 14px;
  color: #9e9e9e;
  font-family: Montserrat-Bold;
  font-weight: 600;
  min-height: 28px;
  padding-top: 8px;
`

export interface RobotItemProps {
  newSub?: SummarySubscription
  robotSerial?: string
  currentRobot?: SummaryRobot | undefined
  newRobot?: SummaryRobot | undefined
  robotsSearch: ReturnType<typeof robotsSearchSelector>
  dispatch: AppDispatch
  showErrors: boolean
  relationId?: string
  taas: boolean
}

export const RobotItemComponent = React.memo(
  ({
    currentRobot,
    newRobot,
    robotsSearch,
    relationId,
    dispatch,
    taas,
    robotSerial
  }: RobotItemProps) => {
    const setNewRobotSerial = (serial: string) =>
      dispatch(setRobotSerial(serial))
    const debouncedSearch = useDebouncedFunction({
      changeHandler: async (robotSerial: string, relationId: string) =>
        await dispatch(searchRobotsDashtransfer({ serial: robotSerial, relationId })),
      delay: 100
    })
    const activeItemNew =
      robotSerial && robotSerial === newRobot?.serial
        ? {
            disabled: false,
            key: newRobot?.serial
          }
        : null
    const handleSelectNewRobot = (serial: string) => {
      const robot: DetailedRobot = robotsSearch.find(r => getRobotRef(r) === serial)
      dispatch(getNewRobot(robot))
    }
    const robotOptions = robotsSearch.map(r => ({
      key: r.taas ? `T-${r.serial}` : r.serial,
      disabled: r.disabled
    }))
    useEffect(() => {
      robotSerial && debouncedSearch(robotSerial, relationId)
    }, [robotSerial, relationId])

    return (
      <>
      <TbDashDetailedItemRow>
        <TbDashDetailedItem
          hasError={v => null}
          type='text'
          disabled
          label='Current serial'
          iconUrl={getHardwareTypeIconPath(currentRobot?.hardwareType)}
          value={currentRobot?.serial ? getRobotRef(currentRobot) : '-'}
        >
        <Label>{getHardwareTypeName(currentRobot?.hardwareType)}</Label>
        </TbDashDetailedItem>
        <TbDashDetailedItem
          hasError={v => null}
          type='autoComplete'
          label='New serial'
          iconUrl=''
          activeItem={activeItemNew}
          onChange={v => {
            setNewRobotSerial(v as string)
          }}
          onSelect={r => r && handleSelectNewRobot(r.key)}
          options={robotOptions}
          value={robotSerial || (newRobot ? getRobotRef(newRobot) : '')}
        >
        <Label>{getHardwareTypeName(newRobot?.hardwareType)}</Label>
        </TbDashDetailedItem>
        <ArrowRightAlt
          fontSize='inherit'
          style={{
            fontSize: 28,
            transform: 'scaleX(-1)',
            position: 'absolute',
            left: '50%',
            marginLeft: 8,
            bottom: 13
          }}
        />
      </TbDashDetailedItemRow>
      <TbDashDetailedItemRow>
      </TbDashDetailedItemRow>
      </>
    )
  }
)

const ConnectedRobotItem = (props: Pick<RobotItemProps, 'showErrors' | 'taas'>) => {
  const { currentRobot, newRobot, newRelation, robotSerial, newSub } = useSelector<
  RootState,
  DashtransferState
  >(dashtransferSelector)
  const robotsSearch = props.taas ? useSelector(robotsTaasSearchSelector) : useSelector(robotsSearchSelector)
  const dispatch: AppDispatch = useDispatch()

  return (
    <RobotItemComponent
      {...props}
      newSub={newSub}
      robotSerial={robotSerial}
      relationId={newRelation?.id?.toString()}
      currentRobot={currentRobot}
      newRobot={newRobot}
      robotsSearch={robotsSearch}
      dispatch={dispatch}
    />
  )
}

export default ConnectedRobotItem
